import React, { useState } from "react"
import useInterval from "../Common/useInterval"
import { WidgetDisplay } from "./WidgetDisplay"
import { WidgetProps } from "./DashboardContext"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import useGetEmailUsage, { EmailUsageResponse } from "../Requests/useGetEmailUsage"

enum Status {
  Good,
  Warning,
  Suspended,
}

export const EmailUsage = (props: WidgetProps) => {
  const refreshTimeMs = 30000
  const [data, setData] = useState<EmailUsageResponse | null>(null)

  const getEmailUsage = useGetEmailUsage({
    onSuccess: (resp) => setData(resp)
  })

  useInterval(() => getEmailUsage.query(), refreshTimeMs, false)

  const formatNumber = (num: number): string => new Intl.NumberFormat('en-US').format(num);

  const getProgressBarColor = (percentage: number): string => {
    if (percentage >= 150) return "bg-danger";
    if (percentage >= 100) return "bg-warning";
    return "bg-success";
  }

  const getProgressBarWidth = (percentage: number): string => `${Math.min(percentage, 100)}%`;

  const status: Status = data &&
    data.percentageUsed >= 150
    ? Status.Suspended
    : (data?.percentageUsed ?? 0) >= 100
      ? Status.Warning
      : Status.Good;

  return (
    <WidgetDisplay
      widgetId={props.id}
      title="Monthly Email Usage"
      isLoading={data === null && getEmailUsage.isLoading}
    >
      <div className="px-4 py-1">
        {getEmailUsage.isError ? (
          <OverlayTrigger
            placement="left"
            delay={{ show: 250, hide: 400 }}
            overlay={
              <Tooltip>
                {getEmailUsage.error?.message ?? "Unable to load email usage data"}
              </Tooltip>
            }>
            <h2 className="h-100 w-100 d-flex justify-content-center align-items-center pb-5">
              No Email Usage Data Available
            </h2>
          </OverlayTrigger>
        ) : data ? (
          <>
            <div>
              <div className="mb-2 row">
                <div className="col-6">
                  <span className="fw-bold col">
                    Monthly Usage
                  </span>
                </div>
                <div className="col-6 text-end">
                  {status === Status.Good && <i className="bi bi-check-circle text-success" />}
                  {status === Status.Warning && <i className="bi bi-exclamation-circle text-warning" />}
                  {status === Status.Suspended && <i className="bi bi-x-circle text-danger" />}
                </div>
              </div>
              <div className="mb-2">
                <h2 className="mb-0 fw-bold">
                  {formatNumber(data.emailsSent)}
                </h2>
                <small className="text-muted fw-bold col">
                  of {formatNumber(data.monthlyLimit)} emails
                </small>
              </div>
              <div className="mb-2">
                <div className="progress">
                  <div
                    className={`progress-bar ${getProgressBarColor(data.percentageUsed)}`}
                    role="progressbar"
                    style={{
                      width: getProgressBarWidth(data.percentageUsed),
                      transition: 'width 0.6s ease'
                    }} />
                </div>
              </div>
              <div className="mb-2">
                <span className="text-muted">
                  {data.percentageUsed.toFixed(1)}% used
                </span>
              </div>
              {status === Status.Suspended && (
                <div className="text-danger small alert alert-soft-danger py-1">
                  <strong>Email service has been suspended. Call support.</strong>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </WidgetDisplay>
  )
}